import { useNavigationPreferences } from '@/hooks/useUserPreferences'
import { normalizeRecordNavHref } from '@/lib/navigation/recordNavHref'
import { useOrganizationStore } from '@/store/organization.store'
import { useUIPreferencesStore } from '@/store/ui-preferences.store'

import type { PinnedNavRouteItem } from '@scrambl/shared'
import { MAX_SIDEBAR_PINS } from '@scrambl/shared'

const EMPTY_MODULE_PINS: string[] = []

/**
 * Number of sidebar pins the user currently holds: server-stored record pins
 * plus client-stored module pins, counted the way the sidebar renders them
 * (record hrefs normalized and de-duplicated; a record pin on a module's own
 * href is that module and counts once).
 */
export function countSidebarPins(
  recordPins: readonly PinnedNavRouteItem[],
  modulePins: readonly string[],
): number {
  const moduleSet = new Set(modulePins)
  const recordHrefs = new Set<string>()
  for (const pin of recordPins) {
    const href = normalizeRecordNavHref(pin.href)
    if (!moduleSet.has(href)) recordHrefs.add(href)
  }
  return recordHrefs.size + moduleSet.size
}

/**
 * Sidebar pin capacity for the current org. Pin entry points disable
 * themselves when `isFull` so the user has to unpin something to add more.
 */
export function useSidebarPinCapacity() {
  const { preferences } = useNavigationPreferences()
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const pinnedModulesByOrg = useUIPreferencesStore(
    state => state.pinnedModulesByOrg,
  )
  const modulePins = organizationId
    ? (pinnedModulesByOrg[organizationId] ?? EMPTY_MODULE_PINS)
    : EMPTY_MODULE_PINS
  const count = countSidebarPins(preferences.pinnedNavItems ?? [], modulePins)
  return {
    count,
    max: MAX_SIDEBAR_PINS,
    isFull: count >= MAX_SIDEBAR_PINS,
  }
}
